import { useState, useRef, useEffect, Fragment } from 'react'
import { graphql } from '@apollo/client/react/hoc'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Badge } from 'reactstrap'
import { AllTasksQuery, BoardQuery } from './Queries'
import { UpdateTaskMutation } from './Mutations'
import TaskDetailsSidePanel from './TaskDetailsSidePanel'
import withApolloClient from '../withApolloClient'
import RepAvatar from '../Material/RepAvatar'
import DatetimeRenderer from '../Material/DatetimeRenderer'
import './TaskKanbanView.css'

const KANBAN_COLUMNS = [
    { key: 'OPEN', label: 'Open', mutationValue: 'OPEN' },
    { key: 'IN_PROGRESS', label: 'In Progress', mutationValue: 'IN PROGRESS' },
    { key: 'DONE', label: 'Done', mutationValue: 'DONE' },
]

function TaskKanbanView({ allTasks, board, loading, apolloClient, boardId, groupBy = 'status' }) {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const selectedTaskId = searchParams.get('task_id')

    const [draggedTaskId, setDraggedTaskId] = useState(null)
    const [dragOverColumn, setDragOverColumn] = useState(null)
    const [localOverrides, setLocalOverrides] = useState({})
    const [cardMenuTaskId, setCardMenuTaskId] = useState(null)
    const [editingTaskId, setEditingTaskId] = useState(null)
    const [editTaskTitle, setEditTaskTitle] = useState("")
    const menuRef = useRef(null)
    const dragJustEndedRef = useRef(false)

    useEffect(() => {
        if (!cardMenuTaskId) return
        const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setCardMenuTaskId(null) }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [cardMenuTaskId])

    const enumColumns = board?.columnSet?.edges
        ?.filter(({ node }) => node.columnType === 'ENUM' && !node.isArchived)
        .map(({ node }) => node) || []

    const computedColumns = (() => {
        if (groupBy === 'status') return KANBAN_COLUMNS
        const col = enumColumns.find(c => String(c.pk) === String(groupBy))
        if (!col) return KANBAN_COLUMNS
        return (col.allowedOptions || []).map(opt => ({ key: opt, label: opt }))
    })()

    const getTaskGroupValue = (task) => {
        if (localOverrides[task.id] !== undefined) return localOverrides[task.id]
        if (groupBy === 'status') {
            return task.status?.toUpperCase().replace(/ /g, '_') || 'OPEN'
        }
        const colVal = task.taskcolumnvalueSet?.edges?.find(
            ({ node }) => String(node.column.pk) === String(groupBy)
        )
        return colVal?.node?.valueString || null
    }

    const tasksByColumn = {}
    computedColumns.forEach(col => { tasksByColumn[col.key] = [] })

    if (allTasks?.edges) {
        allTasks.edges.forEach(({ node }) => {
            const val = getTaskGroupValue(node)
            if (val && tasksByColumn[val] !== undefined) {
                tasksByColumn[val].push(node)
            } else if (tasksByColumn[computedColumns[0]?.key] !== undefined) {
                tasksByColumn[computedColumns[0].key].push(node)
            }
        })
    }

    const handleDrop = async (colKey) => {
        if (!draggedTaskId) return
        const taskId = draggedTaskId
        const taskNode = allTasks?.edges?.find(e => e.node.id === taskId)?.node
        const prevValue = taskNode ? getTaskGroupValue(taskNode) : undefined

        setLocalOverrides(prev => ({ ...prev, [taskId]: colKey }))
        setDragOverColumn(null)
        setDraggedTaskId(null)

        try {
            if (groupBy === 'status') {
                const col = KANBAN_COLUMNS.find(c => c.key === colKey)
                await apolloClient.mutate({
                    mutation: UpdateTaskMutation,
                    variables: { input: { taskId, status: col?.mutationValue || colKey } },
                    refetchQueries: ['AllTasks'],
                })
            } else {
                const enumCol = enumColumns.find(c => String(c.pk) === String(groupBy))
                if (!enumCol) return
                await apolloClient.mutate({
                    mutation: UpdateTaskMutation,
                    variables: { input: { taskId, columnValues: [{ columnId: enumCol.id, value: colKey }] } },
                    refetchQueries: ['AllTasks'],
                })
            }
            setLocalOverrides(prev => { const n = { ...prev }; delete n[taskId]; return n })
        } catch (e) {
            setLocalOverrides(prev => {
                const n = { ...prev }
                if (prevValue !== undefined) {
                    n[taskId] = prevValue
                } else {
                    delete n[taskId]
                }
                return n
            })
        }
    }

    const quickUpdateTask = async (taskId, values) => {
        try {
            await apolloClient.mutate({
                mutation: UpdateTaskMutation,
                variables: { input: { taskId, ...values } },
                refetchQueries: ['AllTasks'],
            })
        } catch (e) {}
    }

    const saveTaskTitle = async (taskId) => {
        const title = editTaskTitle.trim()
        const original = allTasks?.edges?.find(e => e.node.id === taskId)?.node?.title
        if (title && title !== original) await quickUpdateTask(taskId, { title })
        setEditingTaskId(null)
    }

    return (
        <div className="TaskKanbanView">
            {selectedTaskId && <TaskDetailsSidePanel />}
            {loading && <div className="kanban-loading-bar"><div className="kanban-loading-bar-fill" /></div>}
            <div className={`kanban-board${loading ? ' kanban-board--loading' : ''}`}>
                {computedColumns.map(col => (
                    <div
                        key={col.key}
                        className={`kanban-column${dragOverColumn === col.key ? ' drag-over' : ''}`}
                        onDragOver={(e) => { e.preventDefault(); setDragOverColumn(col.key) }}
                        onDragLeave={() => setDragOverColumn(null)}
                        onDrop={() => handleDrop(col.key)}
                    >
                        <div className="kanban-column-header">
                            <span className={`status-label status-${col.key.toLowerCase()}`}>{col.label}</span>
                            <Badge color="secondary" className="ml-2">{(tasksByColumn[col.key] || []).length}</Badge>
                        </div>
                        <div className="kanban-column-body">
                            {(tasksByColumn[col.key] || []).length === 0 && !loading && (
                                <div className="kanban-empty">No tasks</div>
                            )}
                            {(tasksByColumn[col.key] || []).map(task => (
                                <div
                                    key={task.id}
                                    className={`kanban-card${draggedTaskId === task.id ? ' dragging' : ''}`}
                                    draggable={editingTaskId !== task.id}
                                    onDragStart={() => { if (editingTaskId === task.id) return; dragJustEndedRef.current = false; setDraggedTaskId(task.id) }}
                                    onDragEnd={() => { dragJustEndedRef.current = true; setTimeout(() => { dragJustEndedRef.current = false }, 200); setDraggedTaskId(null); setDragOverColumn(null) }}
                                    onClick={() => { if (dragJustEndedRef.current || editingTaskId === task.id) return; navigate(`./?task_id=${task.id}`) }}
                                >
                                    {/* Top row: title + 3-dot menu */}
                                    <div className="kanban-card-top">
                                        {editingTaskId === task.id ? (
                                            <input
                                                className="kanban-title-input"
                                                value={editTaskTitle}
                                                autoFocus
                                                onClick={e => e.stopPropagation()}
                                                onChange={e => setEditTaskTitle(e.target.value)}
                                                onBlur={() => saveTaskTitle(task.id)}
                                                onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') saveTaskTitle(task.id); if (e.key === 'Escape') setEditingTaskId(null) }}
                                            />
                                        ) : (
                                            <div
                                                className="kanban-card-title"
                                                onClick={e => { e.stopPropagation(); setEditTaskTitle(task.title); setEditingTaskId(task.id) }}
                                                title="Click to edit title"
                                            >
                                                {task.title}
                                            </div>
                                        )}
                                        <div
                                            className="kanban-card-menu-btn"
                                            ref={cardMenuTaskId === task.id ? menuRef : null}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <span className="kanban-card-menu-trigger" onClick={() => setCardMenuTaskId(cardMenuTaskId === task.id ? null : task.id)}>
                                                <i className="fa fa-ellipsis-v" />
                                            </span>
                                            {cardMenuTaskId === task.id && (
                                                <div className="kanban-card-menu-dropdown">
                                                    <div className="kanban-card-menu-section-label">Status</div>
                                                    {['OPEN', 'IN PROGRESS', 'DONE'].map(s => (
                                                        <div
                                                            key={s}
                                                            className={`kanban-card-menu-option${task.status?.toUpperCase() === s ? ' active' : ''}`}
                                                            onClick={() => { quickUpdateTask(task.id, { status: s }); setCardMenuTaskId(null) }}
                                                        >{s}</div>
                                                    ))}
                                                    {enumColumns.map(enumCol => (
                                                        <Fragment key={enumCol.pk}>
                                                            <div className="kanban-card-menu-divider" />
                                                            <div className="kanban-card-menu-section-label">{enumCol.title}</div>
                                                            {(enumCol.allowedOptions || []).map(opt => {
                                                                const currentVal = task.taskcolumnvalueSet?.edges?.find(
                                                                    ({ node }) => String(node.column.pk) === String(enumCol.pk)
                                                                )?.node?.valueString
                                                                return (
                                                                    <div
                                                                        key={opt}
                                                                        className={`kanban-card-menu-option${currentVal === opt ? ' active' : ''}`}
                                                                        onClick={() => { quickUpdateTask(task.id, { columnValues: [{ columnId: enumCol.id, value: opt }] }); setCardMenuTaskId(null) }}
                                                                    >{opt}</div>
                                                                )
                                                            })}
                                                        </Fragment>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {/* Bottom row: metadata + stacked avatars */}
                                    <div className="kanban-card-bottom">
                                        <div className="kanban-card-footer">
                                            {task.dueDate && (
                                                <span className="kanban-due-date">
                                                    <i className="fa fa-calendar" aria-hidden="true" />{' '}
                                                    <DatetimeRenderer value={task.dueDate} dateOnly />
                                                </span>
                                            )}
                                            {task.commentsCount > 0 && (
                                                <span className="kanban-comments">
                                                    <i className="fa fa-comments" aria-hidden="true" /> {task.commentsCount}
                                                </span>
                                            )}
                                        </div>
                                        {task.assignedTo?.edges?.length > 0 && (
                                            <div className="kanban-avatars-stack" onClick={e => e.stopPropagation()}>
                                                {task.assignedTo.edges.slice(0, 3).map(({ node: assignee }, idx) => (
                                                    <RepAvatar key={idx} rep={assignee} keepClose />
                                                ))}
                                                {task.assignedTo.edges.length > 3 && (
                                                    <span className="kanban-avatar-overflow">+{task.assignedTo.edges.length - 3}</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default graphql(BoardQuery, {
    skip: ({ boardId }) => !boardId,
    options: ({ boardId }) => ({
        variables: { id: boardId },
        fetchPolicy: 'cache-first',
    }),
    props: ({ data: { board, loading } }) => ({ board, boardLoading: loading }),
})(
    graphql(AllTasksQuery, {
        options: ({ boardId, boardIds, searchTerm, statuses, orderBy, assignedToIds, isArchived, incidentId }) => ({
            variables: {
                boardIds: boardIds ? boardIds : boardId ? [boardId] : null,
                incidentId,
                searchTerm,
                statuses,
                orderBy,
                assignedToIds,
                isArchived,
                first: 100,
            },
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'cache-and-network',
        }),
        props: ({ data: { loading, allTasks, variables } }) => ({
            loading,
            allTasks,
            variables,
        }),
    })(withApolloClient(TaskKanbanView))
)
